/**
 * Menu item image upload – correct Firebase architecture:
 *
 * 1. Upload once → getDownloadURL() once → return URL.
 * 2. Caller saves the URL in Firestore (menu_items.imageUrl / imageUrlThumb).
 * 3. At display time we use the URL from the document directly in <img src={url}>.
 *    No getDownloadURL() at read time = no extra calls, images start loading immediately.
 *
 * Firestore is the single source of truth for image URLs; Storage is just file storage.
 */
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFirebaseStorage } from './firebase';

const STORAGE_PREFIX = 'menu-items';
const FULL_MAX_DIMENSION = 1200;
const THUMB_MAX_DIMENSION = 320;
const FULL_QUALITY = 0.85;
const THUMB_QUALITY = 0.8;

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9.-]/g, '_').slice(0, 80);
}

/**
 * Resize image to max dimension (longest side) and compress as JPEG.
 * Returns original file as Blob if not an image or resize fails.
 */
function resizeToBlob(file: File, maxDimension: number, quality: number): Promise<Blob> {
  if (!file.type.startsWith('image/')) return Promise.resolve(file as unknown as Blob);

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width: w, height: h } = img;
      if (w > maxDimension || h > maxDimension) {
        if (w > h) {
          h = Math.round((h * maxDimension) / w);
          w = maxDimension;
        } else {
          w = Math.round((w * maxDimension) / h);
          h = maxDimension;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file as unknown as Blob);
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : resolve(file as unknown as Blob)),
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file as unknown as Blob);
    };
    img.src = url;
  });
}

export interface MenuItemImageUploadResult {
  /** Full-size image URL (max 1200px). Use for detail view. */
  url: string;
  /** Small thumbnail URL (max 320px). Use for grids/lists for fast loading. */
  thumbUrl: string;
}

/**
 * Upload an image and store both full and thumbnail. Returns both URLs.
 * Use imageUrl for detail/modals, imageUrlThumb for grids to load photos faster.
 */
export async function uploadMenuItemImage(file: File, menuItemId?: number): Promise<MenuItemImageUploadResult> {
  const storage = getFirebaseStorage();
  const pathId = menuItemId && menuItemId > 0 ? String(menuItemId) : 'temp';
  const baseName = sanitizeFilename(file.name.replace(/\.[^.]+$/, ''));
  const ts = Date.now();
  const isImage = file.type.startsWith('image/');

  const [fullBlob, thumbBlob] = await Promise.all([
    resizeToBlob(file, FULL_MAX_DIMENSION, FULL_QUALITY),
    isImage ? resizeToBlob(file, THUMB_MAX_DIMENSION, THUMB_QUALITY) : Promise.resolve(null),
  ]);

  const cacheMetadata = { contentType: 'image/jpeg', cacheControl: 'public, max-age=31536000' };
  const fullRef = ref(storage, `${STORAGE_PREFIX}/${pathId}/${ts}-${baseName}.jpg`);
  await uploadBytes(fullRef, fullBlob, cacheMetadata);
  const url = await getDownloadURL(fullRef);

  let thumbUrl = url;
  if (thumbBlob) {
    if (thumbBlob !== fullBlob && thumbBlob.size < fullBlob.size) {
      const thumbRef = ref(storage, `${STORAGE_PREFIX}/${pathId}/${ts}-${baseName}-thumb.jpg`);
      await uploadBytes(thumbRef, thumbBlob, cacheMetadata);
      thumbUrl = await getDownloadURL(thumbRef);
    }
    // else: image already small, use full URL for both
  }

  return { url, thumbUrl };
}

